import { isDefined } from "@clipboard-health/util-ts";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import {
  SHIFT_DETAILS_CLOCK_IN_QUERY_PARAM,
  SHIFT_DETAILS_FULL_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { WorkBadgeReviewPolicySource } from "../components/Policy/types";

export enum WorkBadgeReviewPolicyStep {
  REVIEW_POLICY = "review_policy",
  SELECT_HEADSHOT = "select_headshot",
  BADGE_READY = "badge_ready",
}

interface UseWorkBadgeReviewPolicyResult {
  step: WorkBadgeReviewPolicyStep;
  setStep: (step: WorkBadgeReviewPolicyStep) => void;
  workerHeadshotBase64: string | undefined;
  setWorkerHeadshotBase64: (value: string | undefined) => void;
  qualificationName: string | undefined;
  handleClose: () => void;
  primaryButtonText: string;
}

export function useWorkBadgeReviewPolicy(): UseWorkBadgeReviewPolicyResult {
  const history = useHistory();
  const searchParams = useSearchParams<{
    source?: WorkBadgeReviewPolicySource;
    qualificationName?: string;
    shiftId?: string;
    step?: WorkBadgeReviewPolicyStep;
  }>();

  const { source, qualificationName, shiftId, step: initialStep } = searchParams;

  const [step, setStep] = useState<WorkBadgeReviewPolicyStep>(
    initialStep ?? WorkBadgeReviewPolicyStep.REVIEW_POLICY
  );

  const [workerHeadshotBase64, setWorkerHeadshotBase64] = useState<string | undefined>();

  const closePath = useMemo(() => {
    if (isDefined(shiftId)) {
      if (source === WorkBadgeReviewPolicySource.BADGE_VERIFICATION) {
        // Users coming from badge verification are in the clock-in stage.
        // Append ?clockIn to auto-resume the flow when navigating back,
        // so they don't need to tap the button again to open badge verification.
        return `${generatePath(SHIFT_DETAILS_FULL_PATH, {
          shiftId,
        })}?${SHIFT_DETAILS_CLOCK_IN_QUERY_PARAM}`;
      }
      if (source === WorkBadgeReviewPolicySource.SHIFT_DETAILS) {
        return generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId });
      }
      if (source === WorkBadgeReviewPolicySource.SHIFT_DISCOVERY) {
        return generatePath(SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH, { shiftId });
      }
    }

    return undefined;
  }, [shiftId, source]);

  const handleClose = useCallback(() => {
    if (isDefined(closePath)) {
      history.replace(closePath);
      return;
    }

    history.goBack();
  }, [history, closePath]);

  const primaryButtonText = useMemo(() => {
    if (
      source === WorkBadgeReviewPolicySource.SHIFT_DETAILS ||
      source === WorkBadgeReviewPolicySource.BADGE_VERIFICATION
    ) {
      return "Go back to Bookings";
    }

    return "Go back to Shifts";
  }, [source]);

  return {
    step,
    setStep,
    workerHeadshotBase64,
    setWorkerHeadshotBase64,
    qualificationName,
    handleClose,
    primaryButtonText,
  };
}
